import React, { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import {
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { auth } from "../firebase";
import { Button, FormField, InputText, theme } from "../components/UI";
import { useAuth } from "../auth/AuthProvider";
import loginBackground from "../assets/ParcelStock.png";

const Login: React.FC = () => {
  const { user, loading, error: accountError, disabledMessage } = useAuth();
  const location = useLocation();
  const from = (location.state as { from?: { pathname?: string } } | undefined)?.from?.pathname || "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!loading && user) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setStatus(null);
    setSubmitting(true);

    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch (err: any) {
      setError(err?.message || "Authentication failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = async () => {
    setError(null);
    setStatus(null);
    if (!email.trim()) {
      setError("Enter your email address first.");
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setStatus("Password reset email sent.");
    } catch (err: any) {
      setError(err?.message || "Failed to send reset email.");
    }
  };

  return (
    <div
      className="ps-login-shell"
      style={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-end",
        padding: theme.spacing.lg,
        paddingBottom: "clamp(64px, 14vh, 200px)",
        backgroundColor: "#0b1020",
        backgroundImage: `url(${loginBackground})`,
      }}
    >
      <div
        className="ps-login-overlay"
        style={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(135deg, rgba(2,6,23,0.75), rgba(30,41,59,0.55), rgba(2,6,23,0.8))",
          pointerEvents: "none",
        }}
      />
      <div
        className="ps-login-form"
        style={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing.md,
        }}
      >

        {disabledMessage && (
          <div style={{ color: theme.colors.danger, fontSize: 13, marginBottom: theme.spacing.sm }}>
            {disabledMessage}
          </div>
        )}
        {accountError && (
          <div style={{ color: theme.colors.danger, fontSize: 13, marginBottom: theme.spacing.sm }}>
            {accountError}
          </div>
        )}

        {error && (
          <div style={{ color: theme.colors.danger, fontSize: 13, marginBottom: theme.spacing.sm }}>
            {error}
          </div>
        )}
        {status && (
          <div style={{ color: theme.colors.success, fontSize: 13, marginBottom: theme.spacing.sm }}>
            {status}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="ps-login-field">
            <FormField label="Email">
              <InputText
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                required
                className="ps-login-input"
              />
            </FormField>
          </div>

          <div className="ps-login-field">
            <FormField label="Password">
              <InputText
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                required
                className="ps-login-input"
              />
            </FormField>
          </div>

          <Button type="submit" disabled={submitting} className="ps-login-button">
            {submitting ? "Working..." : "Sign in"}
          </Button>
        </form>

        <div style={{ marginTop: theme.spacing.sm, fontSize: 13 }}>
          <button type="button" onClick={handleReset} className="ps-login-link">
            Forgot password?
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;
